import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Warning } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { TaskPriorityOptions } from 'shared/types/task-base';

import * as Styled from './task-meta-interaction-priority.style';

export type TaskMetaInteractionPriorityProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value?: TaskPriorityOptions | null;
  onChange: (value: TaskPriorityOptions | null) => void;
  highlight?: boolean;
};

export const TaskMetaInteractionPriority: React.FC<
  TaskMetaInteractionPriorityProps
> = ({ value, onChange, highlight, ...rest }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const onClear = () => {
    onChange(null);
    closeMenu();
  };

  const onHigh = () => {
    onChange(TaskPriorityOptions.High);
    closeMenu();
  };

  const onMedium = () => {
    onChange(TaskPriorityOptions.Medium);
    closeMenu();
  };

  const onLow = () => {
    onChange(TaskPriorityOptions.Low);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={<Icon icon={Warning} />}
        color={highlight && value ? Colors.TextPrimary : Colors.Inherit}
        onClick={openMenu}
        label={value ? t(`task.priority.options.${value}.label`) : undefined}
        tooltipLabel={t(
          value
            ? 'task.priority.tooltip.edit.label'
            : 'task.priority.tooltip.add.label',
        )}
        selected={menuOpen}
        {...rest}
      />

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          onOutside={closeMenu}
          position="right"
          interactionType={InteractionType.Task}
        >
          <PopupMenuList>
            <PopupMenuTitle title={t('task.priority.options.title')} />
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onClear}
                start={!value ? <Icon icon={Check} /> : undefined}
                indentationLevel={!!value ? 1 : 0}
              >
                {t('task.priority.options.none.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onLow}
                start={
                  value === TaskPriorityOptions.Low ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={value !== TaskPriorityOptions.Low ? 1 : 0}
              >
                {t('task.priority.options.low.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onMedium}
                start={
                  value === TaskPriorityOptions.Medium ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={value !== TaskPriorityOptions.Medium ? 1 : 0}
              >
                {t('task.priority.options.medium.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onHigh}
                start={
                  value === TaskPriorityOptions.High ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={value !== TaskPriorityOptions.High ? 1 : 0}
              >
                {t('task.priority.options.high.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
